<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="md"
                   :visible="modal.show"
                   :data="model"
                   :title="$t('Add Telephone Bidder')"
                   @show="onShow"
                   @hidden="onHidden"
                   @ok.prevent="handleSubmit(onSubmit)"
                   :show-confirmation="showConfirmation"
                   :ok-title="$t('Submit')"
                   :is-loading="loading"
                   scrollable>
      <div class="row">
        <div class="col-md-6 offset-3">
          <input-widget :model="model"
                        @keyup.prevent="onPaddleNumberKeyUp"
                        @keydown="onPaddleNumberKeyDown"
                        @blur="onPaddleNumberBlur"
                        type="text"
                        attribute="paddle_number"
                        :autofocus="true"/>
        </div>
      </div>
      <div class="row mb-4">
        <div class="col-md-12">
          <div v-if="bidder">
            <h5 class="mb-0">
              <router-link tabindex="-1"
                           :to="{ name: 'customers.dashboard', params: { id: bidder.customer.uuid }}">
                {{ bidder.customer.display_name }}
              </router-link>
            </h5>
            <p class="mb-0" v-if="bidder.customer && bidder.customer.billing_address"
               v-html="$options.filters.asAddress(bidder.customer.billing_address, false)">
            </p>
            <template v-if="bidder.customer.email">
              <p class="m-0">
                {{ bidder.customer.email }}
              </p>
            </template>
          </div>
        </div>
      </div>
      <div class="row" v-if="errorMessage">
        <div class="col-md-12">
          <b-alert variant="danger" show>
            {{ errorMessage }}
          </b-alert>
        </div>
      </div>
      <div class="row">
        <div class="col-md-4">
          <clerk-select-input-widget :model="model" attribute="preferred_clerk_uuid" type="text" :items="clerks"/>
        </div>
        <div class="col-md-4">
          <language-select-input-widget :model="model" attribute="language_uuid" type="text"/>
        </div>
        <div class="col-md-4">
          <input-widget :model="model" attribute="contact_number" type="text"/>
        </div>
      </div>
      <div class="row mb-2">
        <div class="col-md-12 text-right">
          <b-button variant="link" class="p-0 mt-2" pill @click="addNewLot">
            <font-awesome-icon :icon="'plus-circle'"/>
            {{ $t('Add Lot') }}
          </b-button>
        </div>
      </div>
      <div class="row" v-if="bids.length">
        <div class="col-md-5">
          <label>
            {{ $t('Lot') }}
            <span class="text-danger">*</span>
          </label>
        </div>
        <div class="col-md-6">
          <label>
            {{ $t('Cover Amount') }}
          </label>
        </div>
      </div>
      <div class="row" v-for="(bid, index) in bids" :key="`bid-row-${index}`">
        <div class="col-md-5">
          <input-widget :label="false" :model="bid" :autofocus="lotFocus" attribute="lot_no"/>
        </div>
        <div class="col-md-6">
          <currency-input-widget :label="false"
                                 :model="bid" :currency="modal.sale.currency.symbol"
                                 attribute="cover_amount"/>
        </div>

        <div class="col-md-1">
          <b-tooltip :target="`remove-bid-button-${index}`" :title="$t('Delete')"/>
          <font-awesome-icon class="text-danger hover-cursor"
                             tabindex="-1"
                             :id="`remove-bid-button-${index}`"
                             @click="removeLotByIndex(index)"
                             style="font-size: 1.5em;margin-top: .25em"
                             :icon="'times'"/>
        </div>
      </div>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>

  import InputWidget from "../../../core/components/input-widget/InputWidget.vue";
  import ArtisioModal from "../../../core/components/artisio-modal/ArtisioModal.vue";
  import {createNamespacedHelpers} from "vuex";
  import BiddersService from "../../bidders/BiddersService";
  import TelephoneBidModal from "../models/TelephoneBidModal.model";
  import TelephoneBidModel from "../models/TelephoneBid.model";
  import CurrencyInputWidget from "@/core/widgets/CurrencyInputWidget.vue";
  import ClerkSelectInputWidget from "@/core/widgets/ClerkSelectInputWidget.vue";
  import LanguageSelectInputWidget from "@/core/widgets/LanguageSelectInputWidget.vue";
  import LotsService from "@/modules/lots/LotsService";

  const {mapState, mapActions} = createNamespacedHelpers('telephoneBids');

  export default {
    name: "TelephoneBidModal",
    components: {
      CurrencyInputWidget,
      InputWidget,
      ArtisioModal,
      ClerkSelectInputWidget,
      LanguageSelectInputWidget
    },
    data() {
      return {
        loading: false,
        showConfirmation: true,
        model: new TelephoneBidModal(),
        bidder: null,
        bids: [],
        lots: [],
        clerks: [],
        hasError: false,
        errorMessage: '',
        lotFocus: true,
      }
    },
    computed: {
      ...mapState({
        modal: state => state.modal,
      }),
    },
    methods: {
      ...mapActions(['hideModal', 'createTelephoneBid', 'updateBidder']),
      onShow() {
        this.bidder = null;
        this.showConfirmation = true;
        this.clerks = this.modal.sale.clerks;
        this.getLots();
        this.bids.push(new TelephoneBidModel());
        this.lotFocus = false;
      },
      onHidden() {
        this.model = new TelephoneBidModal();
        this.bids = [];
        this.hideModal();
      },
      async onSubmit() {
        this.hasError = false;
        this.errorMessage = '';

        let telephone_bids = [];
        let requestData = {};

        this.bids.forEach((bid) => {
          let lot_uuid = this.lots.find(lot => lot.lot_no === bid.lot_no) ? this.lots.find(lot => lot.lot_no === bid.lot_no).uuid : '';
          if (!lot_uuid) {
            bid.setErrors('lot_no', [this.$t('Lot not found')]);
            this.hasError = true;
          }
          if (telephone_bids.find(b => b.lot_uuid === lot_uuid)) {
            bid.setErrors('lot_no', [this.$t('Telephone Bid already made with following paddle number for this lot.')]);
            this.hasError = true;
          }
          if (bid.uuid) {
            telephone_bids.push({
              lot_uuid: lot_uuid,
              cover_amount: bid.cover_amount,
              uuid: bid.uuid,
            })
          } else if (lot_uuid) {
            telephone_bids.push({
              lot_uuid: lot_uuid,
              cover_amount: bid.cover_amount
            })
          }

        });

        requestData = {
          bidder_uuid: this.bidder.uuid,
          telephone_bids
        }

        if (this.hasError) {
          return;
        } else {
          this.loading = true;
          let bidderResponse = await this.updateBidder({...this.model, uuid: this.bidder.uuid})
          let response = await this.createTelephoneBid(requestData);
          this.loading = false;

          if (response.success && bidderResponse.success) {
            this.showConfirmation = false;
            this.hideModal();
            this.$toast('success', this.$t('Telephone bid was successfully updated'));
          } else {
            if (response.toastError) {
              this.$errorDialog(response.toastError);
            } else {
              if (response.body.non_field_errors) {
                this.errorMessage = response.body.non_field_errors[0]
              } else {
                for (let index in response.body) {
                  this.bids[index].setMultipleErrors(response.body[index]);
                }
              }
            }
          }
        }
      },
      addNewLot() {
        this.bids.push(new TelephoneBidModel());
        this.lotFocus = true;
      },
      removeLotByIndex(index) {
        for (let i = 0; i < this.bids.length; i++) {
          if (i === index) {
            this.bids.splice(i, 1);
            break;
          }
        }
      },
      onPaddleNumberKeyUp($event) {
        if ($event.keyCode === 13) {
          this.getBidderInfo();
        }
      },
      onPaddleNumberKeyDown($event) {
        if ($event.keyCode === 13) {
          $event.preventDefault();
        }
      },
      onPaddleNumberBlur() {
        if (this.model.paddle_number && (!this.bidder || this.bidder.paddle_number !== this.model.paddle_number)) {
          this.getBidderInfo();
        }
      },
      async getLots() {
        const lotsRes = await LotsService.getLots({auction_uuid: this.modal.sale.uuid});
        let lots = [];
        if (lotsRes.success) {
          lots = lotsRes.body.results;
        }
        this.lots = lots;
      },
      getTelBidLotNo(bids) {
        return bids.map(b => {
          return {
            ...b,
            lot_no: this.lots.find(l => l.uuid === b.lot_uuid) ? this.lots.find(l => l.uuid === b.lot_uuid).lot_no : ''
          }
        })
      },
      async getBidderInfo() {
        this.bids = [];
        this.model.resetErrors('paddle_number')
        let response = await BiddersService.getBidder(this.modal.sale.sale_no, this.model.paddle_number, 'telephone_bids,customer.billing_address,auction.clerks');
        if (response.success) {
          this.bidder = response.body;
          this.model = new TelephoneBidModal(response.body)
          let mutatedLots = this.getTelBidLotNo(response.body.telephone_bids);
          mutatedLots.forEach(b => this.bids.push(new TelephoneBidModel(b)))

          if (!mutatedLots.length) {
            this.bids.push(new TelephoneBidModel());
          }
          this.lotFocus = false;
        } else {
          this.model = new TelephoneBidModal({paddle_number: this.model.paddle_number})
          this.model.paddle_number ? this.model.setErrors('paddle_number', [this.$t('No bidder is assigned to this paddle number')]) : this.model.getFirstError('paddle_number')
          this.bidder = null;
          this.bids.push(new TelephoneBidModel())
        }
      },
    },
  }
</script>

<style scoped>

</style>
